import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import SammiLogo from '../components/common/SammiLogo';
import PaymentForm from '../components/common/PaymentForm';
import OrganizationService from '../services/organization.service';
import { OrganizationData, UserData } from '../types/Registration';

// Initialize Stripe with your publishable key
// TODO: Replace with your actual Stripe publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_12345');

const RegisterPaymentPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [organizationData, setOrganizationData] = useState<OrganizationData | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Load created organization and user data from localStorage
    const createdOrganization = localStorage.getItem('createdOrganization');
    const createdUser = localStorage.getItem('createdUser');
    const setupToken = localStorage.getItem('paymentSetupToken');
    
    if (!createdOrganization || !createdUser || !setupToken) {
      // If no created data or setup token found, redirect to start of registration
      navigate('/register-org');
      return;
    }
    
    try {
      setOrganizationData(JSON.parse(createdOrganization));
      setUserData(JSON.parse(createdUser));
    } catch (error) {
      console.error('Error parsing created data:', error);
      navigate('/register-org');
    }
  }, [navigate]);

  const handlePaymentSuccess = async (paymentMethodId: string) => {
    if (!organizationData || !userData) {
      setErrorMessage('Missing registration data. Please start over.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      // Get the setup token from localStorage
      const setupToken = localStorage.getItem('paymentSetupToken');
      if (!setupToken) {
        setErrorMessage('Setup token expired. Please restart the registration process.');
        return;
      }

      // Use JWT-based payment method attachment during setup
      const result = await OrganizationService.attachPaymentMethodDuringSetup(setupToken, paymentMethodId);
      
      // Clear all stored data including the setup token
      localStorage.removeItem('createdOrganization');
      localStorage.removeItem('createdUser');
      localStorage.removeItem('paymentSetupToken');
      
      // On success, navigate to login with success message
      navigate('/login', { 
        state: { 
          message: 'Registration completed successfully! Please log in to get started.' 
        }
      });
    } catch (error) {
      // Handle specific error cases
      const errorMessage = error instanceof Error ? error.message : '';
      
      if (errorMessage.includes('token') || errorMessage.includes('expired') || errorMessage.includes('invalid')) {
        // Token expired or invalid - clear data and redirect to start
        localStorage.removeItem('createdOrganization');
        localStorage.removeItem('createdUser');
        localStorage.removeItem('paymentSetupToken');
        setErrorMessage('Registration session expired. Please start over.');
        setTimeout(() => navigate('/register-org'), 3000);
      } else {
        setErrorMessage('Failed to complete registration. Please try again.');
      }
      
      console.error('Registration error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentError = (error: string) => {
    setErrorMessage(error);
    setIsSubmitting(false);
  };

  const handleBack = () => {
    navigate('/register-admin');
  };

  if (!organizationData || !userData) {
    return null; // Loading or redirect
  }

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-start py-4 px-4 overflow-auto"
      style={{
        background: 'linear-gradient(135deg, rgba(25, 183, 223, 0.75) 0%, rgba(65, 239, 170, 0.75) 100%), linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}
    >
      <div className="flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-4 md:p-8 lg:p-12 w-full max-w-xl mx-auto my-auto">
        {/* Header */}
        <div className="flex items-center justify-between w-full mb-4 md:mb-6">
          <button
            onClick={handleBack}
            className="flex items-center transition-colors duration-200"
            style={{ color: '#002457' }}
            onMouseEnter={(e) => (e.currentTarget as HTMLButtonElement).style.color = '#41EFAA'}
            onMouseLeave={(e) => (e.currentTarget as HTMLButtonElement).style.color = '#002457'}
            disabled={isSubmitting}
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back
          </button>
        </div>

        <SammiLogo />
        
        <h2 className="text-xl md:text-2xl font-bold mb-1 md:mb-2 text-center" style={{ color: '#002457' }}>
          Payment Information
        </h2>
        <p className="text-gray-600 mb-1 md:mb-2 text-center text-sm md:text-base">
          Step 3 of 3: Secure Payment Setup
        </p>
        <p className="text-sm text-gray-500 mb-1 md:mb-2 text-center">
          For organization: <strong>{organizationData.name}</strong>
        </p>
        <p className="text-xs text-gray-400 mb-6 md:mb-8 text-center">
          Your card will be securely stored but not charged until you add devices
        </p>

        {/* Payment Information */}
        <div className="w-full mb-4 md:mb-6">
          <div className="flex items-center mb-3 md:mb-4">
            <CreditCardIcon className="h-4 md:h-5 w-4 md:w-5 mr-2" style={{ color: '#002457' }} />
            <h3 className="text-base md:text-lg font-semibold" style={{ color: '#002457' }}>
              Secure Payment Method
            </h3>
          </div>
          
          <Elements stripe={stripePromise}>
            <PaymentForm
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              isSubmitting={isSubmitting}
              setIsSubmitting={setIsSubmitting}
              organizationName={organizationData.name}
              customerEmail={userData.email}
            />
          </Elements>
        </div>

        {errorMessage && (
          <div className="mb-4 md:mb-6 p-3 md:p-4 rounded-2xl bg-red-500 text-white text-center text-sm">
            {errorMessage}
          </div>
        )}

        <div className="text-center mt-4 md:mt-6">
          <span className="text-gray-600 text-sm">Already have an account? </span>
          <Link 
            to="/login" 
            className="text-sm transition-colors duration-200"
            style={{ color: '#002457', textDecoration: 'none' }}
            onMouseEnter={(e) => (e.currentTarget as HTMLAnchorElement).style.color = '#41EFAA'}
            onMouseLeave={(e) => (e.currentTarget as HTMLAnchorElement).style.color = '#002457'}
          >
            Sign in here
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPaymentPage;
